import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config';

interface Voice {
  voice_id: string;
  name: string;
  category: string;
  description: string;
  labels: {
    accent?: string;
    age?: string;
    gender?: string;
    use_case?: string;
  };
  preview_url?: string;
}

interface VoiceSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentVoiceId: string;
  onSelectVoice: (voiceId: string, voiceName: string) => void;
}

export default function VoiceSelectorModal({ 
  isOpen, 
  onClose, 
  currentVoiceId,
  onSelectVoice 
}: VoiceSelectorModalProps) {
  const { getToken } = useAuth();
  const [voices, setVoices] = useState<Voice[]>([]);
  const [filteredVoices, setFilteredVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(true);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const [showRecommended, setShowRecommended] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [accentFilter, setAccentFilter] = useState<string>('all');

  const [categories, setCategories] = useState<{
    genders: string[];
    accents: string[];
  }>({ genders: [], accents: [] });

  useEffect(() => {
    if (!isOpen) {
      if (currentAudio) {
        currentAudio.pause();
        setCurrentAudio(null);
      }
      setPlayingVoiceId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    applyFilters();
  }, [voices, searchQuery, genderFilter, accentFilter]);

  const buildApiUrl = (path: string, params: Record<string, string> = {}) => {
    const base = (API_BASE_URL || '').trim().replace(/\/$/, '');
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${base}${formattedPath}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, value);
      }
    });
    return url.toString();
  };

  const fetchVoicePage = async (input: {
    userId: string;
    useRecommended: boolean;
    page: number;
    query: string;
  }) => {
    const endpoint = input.useRecommended
      ? '/api/telemarketing/voices/recommended'
      : '/api/telemarketing/voices';

    const url = buildApiUrl(endpoint, {
      userId: input.userId,
      page: String(input.page),
      pageSize: String(pageSize),
      ...(input.query.trim() ? { search: input.query.trim() } : {})
    });

    const response = await fetch(url);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error?.error || 'Failed to load voices');
    }
    return response.json();
  };

  const loadVoices = async (requestedPage = 1, reset = false, useRecommended = showRecommended) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const userId = await getToken();
      if (!userId) {
        toast.error('Not authenticated');
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const data = await fetchVoicePage({
        userId,
        useRecommended,
        page: requestedPage,
        query: searchQuery
      });

      const voiceList = Array.isArray(data?.voices) ? data.voices : [];
      const nextHasMore = Boolean(data?.hasMore);

      setVoices(prev => {
        const next = reset
          ? voiceList
          : (() => {
              const existing = new Set(prev.map(v => v.voice_id));
              const deduped = voiceList.filter((v: Voice) => !existing.has(v.voice_id));
              return [...prev, ...deduped];
            })();
        extractCategories(next);
        return next;
      });

      setPage(requestedPage);
      setHasMore(nextHasMore);
    } catch (error: any) {
      console.error('[VoiceModal] Load voices error:', error);
      toast.error(error.message || 'Failed to load voices');
      setVoices([]);
      setFilteredVoices([]);
      setHasMore(false);
      setPage(1);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      loadVoices(1, true, showRecommended);
    }, 250);
    return () => clearTimeout(t);
  }, [isOpen, searchQuery, showRecommended]);

  const extractCategories = (voiceList: Voice[]) => {
    const genders = new Set<string>();
    const accents = new Set<string>();
    
    voiceList.forEach(voice => {
      if (voice.labels?.gender) genders.add(voice.labels.gender);
      if (voice.labels?.accent) accents.add(voice.labels.accent);
    });
    
    setCategories({
      genders: Array.from(genders).sort(),
      accents: Array.from(accents).sort()
    });
  };

  const applyFilters = () => {
    let filtered = [...voices];
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(voice => 
        voice.name.toLowerCase().includes(query) ||
        voice.description?.toLowerCase().includes(query)
      );
    }
    
    if (genderFilter !== 'all') {
      filtered = filtered.filter(voice => 
        voice.labels?.gender?.toLowerCase() === genderFilter.toLowerCase()
      );
    }
    
    if (accentFilter !== 'all') {
      filtered = filtered.filter(voice => 
        voice.labels?.accent?.toLowerCase() === accentFilter.toLowerCase()
      );
    }
    
    setFilteredVoices(filtered);
  };

  const playPreview = async (voiceId: string) => {
    try {
      if (currentAudio) {
        currentAudio.pause();
      }
      
      if (playingVoiceId === voiceId) {
        setPlayingVoiceId(null);
        setCurrentAudio(null);
        return;
      }
      
      setPlayingVoiceId(voiceId);
      
      const userId = await getToken();
      if (!userId) {
        toast.error('Not authenticated');
        setPlayingVoiceId(null);
        return;
      }

      const url = buildApiUrl(`/api/telemarketing/voices/${voiceId}/preview`, { userId });
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: "Hello, I'm calling to discuss how our solution can help your business grow."
        })
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to generate preview');
      }
      
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlayingVoiceId(null);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.play();
      setCurrentAudio(audio);
      
    } catch (error: any) {
      console.error('[VoiceModal] Preview error:', error);
      toast.error(error.message || 'Failed to play preview');
      setPlayingVoiceId(null);
    }
  };

  const handleSelectVoice = (voice: Voice) => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setPlayingVoiceId(null);
    onSelectVoice(voice.voice_id, voice.name);
    toast.success(`Voice selected: ${voice.name}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Select AI Voice</h2>
              <p className="text-sm text-gray-600 mt-1">
                Choose the perfect voice for your telemarketing campaign
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search voices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <button
                onClick={() => {
                  setShowRecommended((prev) => {
                    const next = !prev;
                    setVoices([]);
                    setFilteredVoices([]);
                    setPage(1);
                    setHasMore(false);
                    return next;
                  });
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  showRecommended
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-300'
                }`}
              >
                {showRecommended ? '⭐ Recommended' : '📋 All'}
              </button>
            </div>

            <div className="flex gap-4">
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Genders</option>
                {categories.genders.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              
              <select
                value={accentFilter}
                onChange={(e) => setAccentFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Languages / Regions</option>
                {categories.accents.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading voices...</p>
            </div>
          ) : filteredVoices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No voices found matching your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVoices.map(voice => (
                <div
                  key={voice.voice_id}
                  className={`border-2 rounded-lg p-4 transition-all hover:shadow-md ${
                    currentVoiceId === voice.voice_id
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{voice.name}</h3>
                      <div className="flex gap-2 mt-1">
                        {voice.labels?.gender && (
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                            {voice.labels.gender}
                          </span>
                        )}
                        {voice.labels?.accent && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                            {voice.labels.accent}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {voice.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {voice.description}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => playPreview(voice.voice_id)}
                      className="flex-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {playingVoiceId === voice.voice_id ? (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" />
                          </svg>
                          Stop
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                          </svg>
                          Preview
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleSelectVoice(voice)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        currentVoiceId === voice.voice_id
                          ? 'bg-purple-600 text-white'
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {currentVoiceId === voice.voice_id ? '✓ Selected' : 'Select'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && (voices.length > 0 || hasMore) && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => loadVoices(page + 1, false, showRecommended)}
                disabled={!hasMore || loadingMore}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  !hasMore
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : loadingMore
                      ? 'bg-purple-200 text-purple-700 cursor-wait'
                      : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {loadingMore ? 'Loading...' : hasMore ? 'Load more' : 'No more results'}
              </button>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              {filteredVoices.length} voice{filteredVoices.length !== 1 ? 's' : ''} available
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
